// Conversation labels — Phase 6c.
//
// Reads + mutates public.conversation_labels directly from the browser via
// the Supabase anon client. RLS policies enforce that only allowlisted
// users can touch the table, so the backend doesn't need a wrapper route.
//
// Two hooks:
//   - useConversationLabels(id) — list labels on a single conversation
//   - useAddLabel / useRemoveLabel — mutations, both invalidate the labels
//     cache on success
//
// The 8 recommended labels are local constants; the table has no seed.
// Kimi can add arbitrary labels via the UI and we'll render them the same.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type ConversationLabel = {
  id: string;
  conversation_id: string;
  label: string;
  created_at: string;
};

/**
 * Recommended seed list. Rendered as suggestion chips in the LabelEditor
 * popover when the user starts typing (or just opens the menu with no
 * typing). Not persisted — these are just suggestions, not enforced.
 */
export const RECOMMENDED_LABELS = [
  'Refund',
  'Custom Order',
  'Proof Change',
  'Complaint',
  'Brokerage',
  'Damaged',
  'Shipping Issue',
  'Upsell',
] as const;

function keyFor(conversationId: string) {
  return ['conversation-labels', conversationId] as const;
}

export function useConversationLabels(conversationId: string | undefined) {
  return useQuery({
    queryKey: conversationId ? keyFor(conversationId) : ['conversation-labels', 'none'],
    enabled: !!conversationId,
    queryFn: async (): Promise<ConversationLabel[]> => {
      if (!conversationId) return [];
      const { data, error } = await supabase
        .from('conversation_labels')
        .select('id, conversation_id, label, created_at')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ConversationLabel[];
    },
    staleTime: 30_000,
  });
}

export function useAddLabel(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      if (!conversationId) throw new Error('Missing conversationId');
      const trimmed = label.trim();
      if (!trimmed) throw new Error('Label cannot be empty');
      const { error } = await supabase
        .from('conversation_labels')
        .insert({ conversation_id: conversationId, label: trimmed });
      if (error) {
        // UNIQUE violation — already tagged, not a real error for us.
        if (error.code === '23505') return;
        throw error;
      }
    },
    onSuccess: () => {
      if (!conversationId) return;
      qc.invalidateQueries({ queryKey: keyFor(conversationId) });
    },
  });
}

export function useRemoveLabel(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (labelId: string) => {
      const { error } = await supabase
        .from('conversation_labels')
        .delete()
        .eq('id', labelId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!conversationId) return;
      qc.invalidateQueries({ queryKey: keyFor(conversationId) });
    },
  });
}
